import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Upload,
  Image as ImageIcon,
  VideoCamera,
  Trash,
  Eye,
  Heart,
  Coins,
  ChartBar,
  Plus,
  X,
  FileArrowUp,
  Tag,
  Lock,
  Users,
  TrendUp,
  CalendarBlank,
  Broadcast,
  Package,
  Link as LinkIcon,
  Star as StarIcon,
  FlowerLotus
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import StreamingTab from '../components/creator/StreamingTab';
import BundlesTab from '../components/creator/BundlesTab';
import ExternalSubsTab from '../components/creator/ExternalSubsTab';
import FanTiersTab from '../components/creator/FanTiersTab';
import ToysTab from '../components/creator/ToysTab';

const API = process.env.REACT_APP_BACKEND_URL;

const CreatorDashboardPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [content, setContent] = useState([]);
  const [contentTotal, setContentTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const fileRef = useRef(null);

  // Upload form
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadPrice, setUploadPrice] = useState(0);
  const [uploadTags, setUploadTags] = useState('');
  const [uploadSubOnly, setUploadSubOnly] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/analytics/dashboard`, { withCredentials: true });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics');
    }
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/upload/my-content`, { withCredentials: true });
      setContent(res.data.items);
      setContentTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch content');
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchAnalytics(), fetchContent()]);
      setLoading(false);
    };
    load();
  }, [fetchAnalytics, fetchContent]);

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle.trim()) {
      toast.error('Please select a file and enter a title');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('title', uploadTitle);
    formData.append('description', uploadDesc);
    formData.append('price_tokens', uploadPrice.toString());
    formData.append('is_subscriber_only', uploadSubOnly.toString());
    formData.append('tags', uploadTags);

    try {
      await axios.post(`${API}/api/upload/content`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          setUploadProgress(Math.round((e.loaded * 100) / (e.total || 1)));
        },
      });
      toast.success('Content uploaded!');
      setUploadOpen(false);
      resetUploadForm();
      fetchContent();
      fetchAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this content permanently?')) return;
    try {
      await axios.delete(`${API}/api/upload/content/${id}`, { withCredentials: true });
      toast.success('Content deleted');
      fetchContent();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadTitle('');
    setUploadDesc('');
    setUploadPrice(0);
    setUploadTags('');
    setUploadSubOnly(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Redirect non-creators (after all hooks)
  if (user && user.role !== 'creator' && user.role !== 'admin') {
    return <Navigate to="/feed" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="creator-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Creator Dashboard</h1>
          <p className="text-slate-400 mt-1">Manage your content and track performance</p>
        </div>
        <Dialog open={uploadOpen} onOpenChange={(v) => { setUploadOpen(v); if (!v) resetUploadForm(); }}>
          <DialogTrigger asChild>
            <button
              className="px-5 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors flex items-center gap-2"
              data-testid="upload-content-btn"
            >
              <Plus className="w-5 h-5" />
              Upload Content
            </button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg" aria-describedby="upload-desc">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Upload Content</DialogTitle>
              <p id="upload-desc" className="text-sm text-slate-400">Share photos or videos with your subscribers</p>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* File Drop */}
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500/50 transition-colors"
                data-testid="file-drop-zone"
              >
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-3">
                    {uploadFile.type.startsWith('video') ? (
                      <VideoCamera className="w-8 h-8 text-blue-400" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-blue-400" />
                    )}
                    <div className="text-left">
                      <p className="text-white font-medium truncate max-w-[200px]">{uploadFile.name}</p>
                      <p className="text-xs text-slate-500">{formatFileSize(uploadFile.size)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}
                      className="p-1 hover:bg-white/10 rounded-full"
                    >
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                ) : (
                  <>
                    <FileArrowUp className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm">Click to select a file</p>
                    <p className="text-slate-600 text-xs mt-1">JPG, PNG, GIF, WebP, MP4, WebM (max 100MB)</p>
                  </>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
                  className="hidden"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  data-testid="file-input"
                />
              </div>

              <div>
                <Label>Title</Label>
                <Input
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Give your content a title"
                  className="bg-slate-800 border-white/10 mt-1"
                  data-testid="upload-title"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={uploadDesc}
                  onChange={(e) => setUploadDesc(e.target.value)}
                  placeholder="Optional description..."
                  className="bg-slate-800 border-white/10 mt-1"
                  data-testid="upload-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price (tokens)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={uploadPrice}
                    onChange={(e) => setUploadPrice(parseInt(e.target.value) || 0)}
                    className="bg-slate-800 border-white/10 mt-1"
                    data-testid="upload-price"
                  />
                  <p className="text-xs text-slate-600 mt-1">0 = free content</p>
                </div>
                <div>
                  <Label>Tags</Label>
                  <Input
                    value={uploadTags}
                    onChange={(e) => setUploadTags(e.target.value)}
                    placeholder="tag1, tag2, tag3"
                    className="bg-slate-800 border-white/10 mt-1"
                    data-testid="upload-tags"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={uploadSubOnly}
                  onChange={(e) => setUploadSubOnly(e.target.checked)}
                  className="w-4 h-4 rounded bg-slate-800 border-white/10 accent-blue-500"
                  data-testid="upload-sub-only"
                />
                <span className="text-sm text-slate-300 flex items-center gap-1">
                  <Lock className="w-4 h-4" />
                  Subscribers only
                </span>
              </label>

              {/* Upload Progress */}
              {uploading && (
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setUploadOpen(false); resetUploadForm(); }}
                  className="flex-1 py-3 border border-white/10 text-white rounded-full font-medium hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !uploadFile || !uploadTitle.trim()}
                  className="flex-1 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="upload-submit-btn"
                >
                  {uploading ? `Uploading ${uploadProgress}%...` : 'Upload'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { key: 'overview', label: 'Overview', icon: ChartBar },
          { key: 'content', label: 'Content', icon: ImageIcon },
          { key: 'streaming', label: 'Streaming', icon: Broadcast },
          { key: 'toys', label: 'Toys (Lovense)', icon: FlowerLotus },
          { key: 'bundles', label: 'Bundles', icon: Package },
          { key: 'fantiers', label: 'Fan Tiers', icon: StarIcon },
          { key: 'externalsubs', label: 'External Subs', icon: LinkIcon },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
              activeTab === key ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
            data-testid={`tab-${key}`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && analytics && (
        <div>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="glass-light rounded-xl p-4">
              <ChartBar className="w-7 h-7 text-blue-400 mb-2" />
              <div className="text-2xl font-bold text-white">{analytics.total_content}</div>
              <div className="text-sm text-slate-400">Content</div>
            </div>
            <div className="glass-light rounded-xl p-4">
              <Heart className="w-7 h-7 text-blue-400 mb-2" />
              <div className="text-2xl font-bold text-white">{analytics.total_likes}</div>
              <div className="text-sm text-slate-400">Likes</div>
            </div>
            <div className="glass-light rounded-xl p-4">
              <Eye className="w-7 h-7 text-blue-400 mb-2" />
              <div className="text-2xl font-bold text-white">{analytics.total_views}</div>
              <div className="text-sm text-slate-400">Views</div>
            </div>
            <div className="glass-light rounded-xl p-4">
              <Users className="w-7 h-7 text-green-400 mb-2" />
              <div className="text-2xl font-bold text-white">{analytics.total_subscribers}</div>
              <div className="text-sm text-slate-400">Subscribers</div>
            </div>
            <div className="glass-light rounded-xl p-4">
              <Coins className="w-7 h-7 text-amber-400 mb-2" />
              <div className="text-2xl font-bold text-white">{analytics.token_earnings}</div>
              <div className="text-sm text-slate-400">Token Earnings</div>
            </div>
            <div className="glass-light rounded-xl p-4">
              <TrendUp className="w-7 h-7 text-emerald-400 mb-2" />
              <div className="text-2xl font-bold text-white">${analytics.estimated_revenue}</div>
              <div className="text-sm text-slate-400">Est. Revenue</div>
            </div>
          </div>

          {/* Weekly Summary */}
          <div className="glass-light rounded-2xl p-6 mb-6">
            <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
              <CalendarBlank className="w-5 h-5 text-slate-400" />
              This Week
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="text-xl font-bold text-white">{analytics.weekly.new_content}</div>
                <div className="text-sm text-slate-400">New uploads</div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="text-xl font-bold text-white">{analytics.weekly.new_subscribers}</div>
                <div className="text-sm text-slate-400">New subscribers</div>
              </div>
            </div>
          </div>

          {/* Recent Content Performance */}
          {analytics.recent_content.length > 0 && (
            <div className="glass-light rounded-2xl p-6">
              <h3 className="font-heading text-lg font-bold text-white mb-4">Recent Performance</h3>
              <div className="space-y-3">
                {analytics.recent_content.map(item => (
                  <div key={item.id} className="flex items-center justify-between bg-slate-800/50 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      {item.content_type === 'video' ? (
                        <VideoCamera className="w-5 h-5 text-blue-400" />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-blue-400" />
                      )}
                      <span className="text-white font-medium truncate max-w-[200px]">{item.title}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-slate-400 flex items-center gap-1">
                        <Heart className="w-4 h-4" /> {item.likes}
                      </span>
                      <span className="text-slate-400 flex items-center gap-1">
                        <Eye className="w-4 h-4" /> {item.views}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div>
          <p className="text-slate-400 mb-4">{contentTotal} items</p>
          {content.length === 0 ? (
            <div className="glass-light rounded-2xl p-12 text-center">
              <Upload className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 mb-2">No content yet</p>
              <p className="text-slate-600 text-sm">Upload your first photo or video to get started!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {content.map((item, idx) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className="glass-light rounded-2xl overflow-hidden"
                  data-testid={`content-card-${item.id}`}
                >
                  {/* Preview */}
                  <div className="aspect-video bg-slate-900 relative">
                    {item.content_type === 'image' ? (
                      <img src={`${API}${item.media_url}`} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <VideoCamera className="w-12 h-12 text-slate-600" />
                      </div>
                    )}
                    {item.price_tokens > 0 && (
                      <span className="absolute top-2 right-2 px-2 py-1 bg-amber-500/90 text-white text-xs font-bold rounded-full flex items-center gap-1">
                        <Coins className="w-3 h-3" /> {item.price_tokens}
                      </span>
                    )}
                    {item.is_subscriber_only && (
                      <span className="absolute top-2 left-2 px-2 py-1 bg-blue-500/90 text-white text-xs font-bold rounded-full flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Sub Only
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h4 className="font-medium text-white truncate">{item.title}</h4>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {item.likes_count}</span>
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {item.views_count}</span>
                        <span>{formatFileSize(item.file_size)}</span>
                      </div>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        data-testid={`delete-${item.id}`}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                    {item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {item.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="text-[10px] px-2 py-0.5 bg-slate-800 text-slate-500 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Streaming Tab */}
      {activeTab === 'streaming' && <StreamingTab />}

      {/* Toys (Lovense) Tab */}
      {activeTab === 'toys' && <ToysTab />}

      {/* Bundles Tab */}
      {activeTab === 'bundles' && <BundlesTab />}

      {/* Fan Tiers Tab */}
      {activeTab === 'fantiers' && <FanTiersTab />}

      {/* External Subs Tab */}
      {activeTab === 'externalsubs' && <ExternalSubsTab />}
    </div>
  );
};

export default CreatorDashboardPage;
